/**
 * abilityColorHelpers — pure color resolution helpers for ability VFX.
 *
 * Each helper takes a `resolveColor` function parameter so they are fully
 * unit-testable without requiring the AccessibilitySystem singleton.
 *
 * Usage:
 *   import { a11yColor3, a11yColor4 } from '@babylon/systems/abilityColorHelpers';
 *   const rc = (k: string, f: string) => AccessibilitySystem.getInstance().resolveColor(k, f);
 *   const c3 = a11yColor3('MINNA_AURA', '#00ff88', rc);
 *   const c4 = a11yColor4('MINNA_AURA', '#00ff88', 0.9, rc);
 *
 * For colors whose pre-Phase-4 value was a raw Color4/Color3 literal (not a
 * COLORS.X palette reference), use the FromRgb variants so 'none' mode
 * returns values byte-identical to the original literals:
 *   const c4 = a11yColor4FromRgb('BRASS', 1.0, 0.82, 0.4, 1.0, rc);
 *   const c3 = a11yColor3FromRgb('EMERALD', 0.0, 1.0, 0.4, rc);
 */
import { Color3, Color4 } from "@babylonjs/core/Maths/math.color";

/**
 * Resolve a named ability color key through the current colorblind override
 * and return it as a Color4 with the given alpha.
 *
 * The alpha is passed separately (not embedded in the hex) so the caller
 * controls transparency independently from the palette key.
 *
 * @param key         - COLORBLIND_OVERRIDES key (e.g. 'MINNA_AURA')
 * @param fallbackHex - Hex color used when no override is active (e.g. '#00ff88')
 * @param alpha       - Alpha channel [0, 1]
 * @param resolveColor - Injected resolver: (key, fallback) => hex string
 */
export function a11yColor4(
  key: string,
  fallbackHex: string,
  alpha: number,
  resolveColor: (k: string, f: string) => string,
): Color4 {
  const hex = resolveColor(key, fallbackHex);
  const c3 = Color3.FromHexString(hex);
  return new Color4(c3.r, c3.g, c3.b, alpha);
}

/**
 * Resolve a named ability color key through the current colorblind override
 * and return it as a Color3.
 *
 * @param key         - COLORBLIND_OVERRIDES key (e.g. 'EMERALD')
 * @param fallbackHex - Hex color used when no override is active (e.g. '#00cc88')
 * @param resolveColor - Injected resolver: (key, fallback) => hex string
 */
export function a11yColor3(
  key: string,
  fallbackHex: string,
  resolveColor: (k: string, f: string) => string,
): Color3 {
  const hex = resolveColor(key, fallbackHex);
  return Color3.FromHexString(hex);
}

/**
 * Like a11yColor4 but the no-override ('none' mode) fallback is expressed as
 * explicit r,g,b values in [0,1] rather than a hex string.  This ensures that
 * 'none' mode produces values byte-identical to the original Color4 literals
 * that existed before the Phase-4 colorblind routing was added.
 *
 * When an override IS active, resolveColor returns a non-empty hex string that
 * is parsed normally — override colors only change the hue, not the alpha.
 *
 * @param key         - COLORBLIND_OVERRIDES key (e.g. 'BRASS')
 * @param r           - Original red channel [0, 1]
 * @param g           - Original green channel [0, 1]
 * @param b           - Original blue channel [0, 1]
 * @param alpha       - Alpha channel [0, 1] (always from caller, not from hex)
 * @param resolveColor - Injected resolver: (key, fallback) => hex string or ""
 */
export function a11yColor4FromRgb(
  key: string,
  r: number,
  g: number,
  b: number,
  alpha: number,
  resolveColor: (k: string, f: string) => string,
): Color4 {
  // Pass "" as the fallback sentinel so we can detect when no override exists.
  const override = resolveColor(key, "");
  if (!override) {
    // No colorblind override — return the exact original literal values.
    return new Color4(r, g, b, alpha);
  }
  // An override hex is active — parse it and keep the caller's alpha.
  const c3 = Color3.FromHexString(override);
  return new Color4(c3.r, c3.g, c3.b, alpha);
}

/**
 * Like a11yColor3 but the no-override ('none' mode) fallback is expressed as
 * explicit r,g,b values in [0,1].  Keeps 'none' mode byte-identical to the
 * original Color3 literals.
 *
 * @param key         - COLORBLIND_OVERRIDES key (e.g. 'EMERALD')
 * @param r           - Original red channel [0, 1]
 * @param g           - Original green channel [0, 1]
 * @param b           - Original blue channel [0, 1]
 * @param resolveColor - Injected resolver: (key, fallback) => hex string or ""
 */
export function a11yColor3FromRgb(
  key: string,
  r: number,
  g: number,
  b: number,
  resolveColor: (k: string, f: string) => string,
): Color3 {
  const override = resolveColor(key, "");
  if (!override) {
    return new Color3(r, g, b);
  }
  return Color3.FromHexString(override);
}
